<template>
  <div
    id="itemlist"
    ref="itemlist"
  >
    <header-layout />
    <div v-if="apiOfferError || apiSearchError">
      <component
        :is="errorComponent"
        class="d-print-none"
      />
    </div>
    <div v-else>
      <!-- <pre>experimentHideHeroImg: {{ experimentHideHeroImg }}</pre> -->
      <visible-header-wrapper>
        <div
          v-if="locationBreadcrumbs.length > 0"
          class="breadcrumb-row breadcrumb-top"
        >
          <breadcrumb
            :items="locationBreadcrumbs"
            :is-seo-page="!experimentHideHeroImg && (isSeoPage || isSeaPage)"
            :header-search-subtitle="experimentHideHeroImg ? headerSearchSubtitle : ''"
          />
        </div>
        <header-search
          v-if="!experimentHideHeroImg && (isSeoPage || isSeaPage)"
          :has-seo-content="seoContent.text1 !== ''"
          :header-search-title-html="headerSearchTitleHtml | replaceHeart"
          :header-search-subtitle="headerSearchSubtitle"
          :multi-headline="showMultiHeadline"
          :header-search-country="country"
          :header-search-region="region"
          :only-fill-store="true"
          :location-title="locationTitle"
          :hide-trust-elements="fullscreenmap"
          :header-search-query="searchQuery"
          :offer-id="offerID"
        />
        <search-content-top
          v-if="isSeoPage"
          :content="seoContent"
        />
      </visible-header-wrapper>
      <div
        :id="smallDevice ? 'searchResult' : ''"
      >
        <search-filter-summary
          :location-breadcrumbs="locationBreadcrumbs"
        />
      </div>
      <search-filter-opener
        @open-map="openModalMap"
      />
      <lazy-hydrate
        never
        :trigger-hydration="searchModalInitialized"
      >
        <server-side-modal
          v-if="searchModalInitialized"
          ref="searchModal"
          :modal-name="'searchModal'"
          :component-name="'SearchModalSearch'"
          class="SearchModalSearch"
          :scrollable="false"
          @ssr-modal-ready="searchModalReady = true"
        />
      </lazy-hydrate>
      <!--<div>
      Mapbounds {{ mapBounds }}
    </div>-->
      <div
        :id="!smallDevice ? 'searchResult' : '' "
        class="search"
      >
        <div class="search-list">
          <search-filter-menu
            v-show="!fullscreenmap"
            ref="searchFilterMenu"
            @open-map="openModalMap"
          />
          <!-- .overgrey is overlay state for popover search filters -->
          <div
            v-observer="{ name: 'sol', callback: preloadObjectPage }"
            class="search-list-objects"
            :class="{overgrey:greyedStatus}"
          >
            <search-object-list
              ref="searchObjectList"
              :is-loading="!ready"
              :stoprender="fullscreenmap || showModalStatus"
              :is-seo-page="isSeoPage || isSeaPage"
            />
          </div>
          <div class="grid-criterias-pagination">
            <div class="grid-item-criterias-pagination">
              <notice-sorting-criterias />
            </div>
            <div class="grid-item-criterias-pagination">
              <search-list-pagination v-if="ready" />
            </div>
          </div>
        </div>
        <component-observer
          v-if="showSearchMap"
          class="search-map-observer d-none d-lg-block"
          :class="{inviewport:inviewport}"
          :body-style="{ position: 'sticky', top: 0 }"
          :root-margin="'0px'"
          :placeholder-style="{ height: '50vh', width: '90%', marginLeft: '5%', marginTop: '5%' }"
          use-idle-callback
          @component-intersected="inviewport = true"
        >
          <div
            v-if="inviewport"
            class="search-map "
            @close-map="toggleMap"
          >
            <!-- need to gate map behind readyness of full page -->
            <!-- because it renders wrong if page is still hidden -->
            <search-map
              ref="searchMap"
              map-name="searchMap"
              :zoom="6"
              :parent-page="'search'"
              :bounds-param="mapBounds"
              :bounds-source="boundsSource"
              :inview="inviewport"
            />
          </div>
        </component-observer>
        <modal
          v-if="mapModalReady"
          name="mapModal"
          class="searchMapModal"
          height="100%"
          width="100%"
          :scrollable="false"
        >
          <search-map
            ref="searchMapModal"
            name="searchMapModal"
            :zoom="6"
            :parent-page="'search'"
            :highlight-object="highlightObject"
            :bounds-param="mapBounds"
            :bounds-source="boundsSource"
            :inview="inviewport"
            :modal="true"
          />
        </modal>
      </div>
      <search-content-bottom
        v-if="(isSeoPage || isSeaPage)"
        :content="seoContent"
      />
      <portal-target name="header-search-picker-modal" />
    </div>
  </div>
</template>

<script>
/** COMPONENTS */
import HeaderLayout from 'components/Header.vue';
import LazyHydrate from 'vue-lazy-hydration';
import hydrateOrObserveWhenVisible from 'utils/hydrateOrObserveWhenVisible';
import VisibleHeaderWrapper from 'components/VisibleHeaderWrapper.vue';
import Breadcrumb from 'components/Breadcrumb.vue';
import HeaderSearch from 'components/HeaderSearch.vue';
import SearchContentTop from 'components/SearchContentTop.vue';
import ServerSideModal from 'components/ServerSideModal.vue';
import NoticeSortingCriterias from 'components/NoticeSortingCriterias.vue';

import ComponentObserver from 'components/loading/ComponentObserver.vue';
import SearchFilterMenuLoading from 'components/loading/SearchFilterMenuLoading.vue';
import SearchFilterOpenerLoading from 'components/loading/SearchFilterOpenerLoading.vue';
import SearchFilterSummaryLoading from 'components/loading/SearchFilterSummaryLoading.vue';
import IntersectionObserverDirective from 'mixins/IntersectionObserver';

/** STORE */
import { mapMutations, mapGetters } from 'vuex';

/** MIXINS */
import searchSetup from 'mixins/composition/search/index';
import FlatpickrSearchFunctions from 'mixins/FlatpickrSearchFunctions';
import { onMounted, onUnmounted } from '@vue/composition-api';
import GtmTools from 'mixins/GtmTools';
import TrackingData from 'mixins/TrackingData';
import ResetApiHttpStatus from 'mixins/ResetApiHttpStatus';
import UserApi from 'mixins/UserApi';
import hydrationPreventionSetup from 'mixins/composition/search/HeaderSearchHydrationPrevention';
import { trackSearch } from 'requests/searchObjects';
import Error404 from 'components/errors/Error404Content.vue';
import Error500 from 'components/errors/Error500Content.vue';

import {
  getCookieVal,
} from 'mixins/composition/CookieApi';

const lazyOptions = {
  observerOptions: {
    rootMargin: '500px',
  },
};

/** PRELOAD */
// import { ObjectPage } from 'router/routes/all';

export default {
  name: 'Search',
  metaInfo() {
    return this.metaData;
  },
  components: {
    HeaderLayout,
    LazyHydrate,
    VisibleHeaderWrapper,
    Breadcrumb,
    HeaderSearch,
    SearchContentTop,
    ComponentObserver,
    SearchFilterSummary: hydrateOrObserveWhenVisible(() => import(/* webpackChunkName: "lazyhydrate-sfs" */ 'components/SearchFilterSummary.vue'), { loadingComponent: SearchFilterSummaryLoading }),
    SearchFilterOpener: hydrateOrObserveWhenVisible(() => import(/* webpackChunkName: "lazyhydrate-sfo" */ 'components/SearchFilterOpener.vue'), { loadingComponent: SearchFilterOpenerLoading }),
    ServerSideModal,
    SearchFilterMenu: hydrateOrObserveWhenVisible(() => import(/* webpackChunkName: "lazyhydrate-sfm" */ 'components/SearchFilterMenu.vue'), { loadingComponent: SearchFilterMenuLoading }),
    SearchObjectList: hydrateOrObserveWhenVisible(() => import(/* webpackChunkName: "lazyhydrate-sol" */ 'components/SearchObjectList.vue'), {
      loadingComponentData: {
        props: {
          placeholderStyle: {
            height: '1000px',
          },
        },
      },
    }),
    SearchListPagination: hydrateOrObserveWhenVisible(() => import(/* webpackChunkName: "lazyhydrate-slp" */ 'components/SearchListPagination.vue'), { ...lazyOptions, loadingComponent: false }),
    SearchMap: () => import(/* webpackChunkName: "lazychunk-searchmap" */ 'components/SearchMap.vue'),
    SearchContentBottom: () => import(/* webpackChunkName: "lazychunk-searchcontentbottom" */ 'components/SearchContentBottom.vue'),
    Error404,
    Error500,
    NoticeSortingCriterias
  },
  // include searchFunctions
  mixins: [FlatpickrSearchFunctions, GtmTools, TrackingData, ResetApiHttpStatus, UserApi, IntersectionObserverDirective],
  setup(props, ctx) {
    const { watchHydration, searchObjectsMounted, dispatchHydrateAction } = hydrationPreventionSetup(ctx.root.$store, ctx.root, ctx.refs);
    const searchSetupValues = searchSetup(ctx);
    const {
      // watchForRadiusSearch,
      watchForModalChanges, setupMediaQueries, killMediaListeners,
    } = searchSetupValues.setup;

    watchForModalChanges();
    // watchForRadiusSearch();
    setupMediaQueries();
    onMounted(() => {
      setupMediaQueries();
      watchHydration('searchObjectList');
      watchHydration('searchFilterMenu');
    });
    onUnmounted(() => { killMediaListeners(); });
    return {
      ...searchSetupValues.data,
      ...searchSetupValues.methods,
      searchObjectsMounted,
      dispatchHydrateAction,
      /*
      list of values initialized from setup()

      data:
        metaData
        country
        region
        isSeaPage
        isSeoPage
        isPlace
        totalObjects
        ready
        offerID
        seoContent
        locationTitle
        locationBreadcrumbs
        fullscreenmap
        showModalStatus
        headerSearchSubtitle
        showMultiHeadline
        headerSearchTitleHtml
        breakpoint
        searchModalInitialized
        searchModalReady
        mapBounds
        boundsSource
      methods:
        searchRedirect
        formatNumber
      */
    };
  },
  i18nOptions: { namespaces: ['common', 'errors', 'filter', 'agency'] },
  data() {
    return {
      inviewport: false,
      searchTerms: {},
      highlightObject: false,
      mapModalReady: false,
      overgreyed: false,
      // boundsSource: null,
      // mapBounds: null,
      boundstimeout: null,
      dataLayerReady: false,
    };
  },
  serverPrefetch() {
    return this.$store.dispatch('SEARCH_OBJECTS', { router: this.$router }).catch((e) => {
      // set ssr status so express can render the page with the error http code
      this.$store.commit('SET_APP_HTTPSTATUS', e.status);
      this.$store.commit('SET_APP_APIHTTPSTATUS', { type: 'search', status: e.status });
      // reset filters because SEARCH_OBJECTS check filters to see whether somethign changes
      // not sure this is necessary for SSR, but won't hurt either
      this.$store.commit('SET_LASTSEARCHFILTER', {});
    });
  },
  computed: {
    ...mapGetters(['getSearchOvergrey']),
    // used in the header search query to set searchcritera
    searchQuery() {
      let obj = {};
      if (this.$route.path === '/ferienhausbewertungen/') {
        obj = { rating: 1 };
      }
      return obj;
    },
    greyedStatus() {
      return this.getSearchOvergrey;
    },
    smallDevice() { return ['xs', 'sm'].includes(this.breakpoint); },
    apiSearchError() {
      return this.$store.getters.getApiHttpStatus('search') !== 200;
    },
    apiOfferError() {
      return this.$store.getters.getApiHttpStatus('offer') !== 200;
    },
    searchTrackingUID() {
      let uid = this.$store.getters.getUid;
      if (!uid) {
        uid = getCookieVal('uid');
      }
      return uid;
    },
    searchTrackingVars() {
      return this.$store.getters.getSearchTrackVars;
    },
    searchTotalObjects() {
      return this.$store.state.searchTotalObjects || 0;
    },
    searchObjectsNumbers() {
      return this.$store.getters.getSearchObjectsNumbers;
    },
    // EDMT-5185 returns if hero img should be hidden
    experimentHideHeroImg() {
      if (Object.keys(this.$route.query).indexOf('hero') > -1 && this.$route.query.hero.toString() === 'n') {
        return true;
      }
      // const experiment = this.$store.getters.getExperimentById('experiment_edmt-5185');
      // const variant = parseInt(experiment?.variant, 10);
      // if (this.isSeaPage && variant === 1) {
      //   return true;
      // }
      return false;
    },
    showSearchMap() {
      if (this.$store.getters.getPartnerConfigValue('showSearchMap') === 'N') return false;
      return true;
    },
    errorComponent() {
      let errorPage = '';
      if (this.apiOfferError) {
        // differentiate between 500's and 400's to render correct error page
        let renderedPage = 'error404';
        const offerStatus = this.$store.getters.getApiHttpStatus('offer');
        // eslint-disable-next-line prefer-regex-literals
        const checkForServerError = new RegExp(/5\d{2}$/);
        if (checkForServerError.test(offerStatus)) {
          renderedPage = 'error500';
        }
        errorPage = renderedPage;
      } if (this.apiSearchError) {
        errorPage = 'error500';
      }
      return errorPage;
    },
  },
  watch: {
    $route: [{ // watch route change and trigger search
      immediate: false,
      handler: 'search',
    }],
    breakpoint() {
      if (['xs', 'sm'].includes(this.breakpoint)) {
        this.$nextTick(() => {
          this.setMapStatus({ fullscreen: false }); // init false, prevents show map immediatly
        });
      }
    },
    searchTrackingVars: {
      handler: 'setSearchTracking',
    },
    searchTrackingUID: {
      handler: 'setSearchTracking',
      immediate: true,
    },
  },
  mounted() {
    this.clearOverflow();

    this.$nextTick(() => {
      if (!this.apiOfferError && !this.apiSearchError) {
        this.search(false);
      }
    });
  },
  destroyed() {
    if (document) {
      document.querySelector('html').classList.remove('scroll-off');
    }
  },
  methods: {
    setSearchTracking() {
      if (this.searchTrackingUID !== '' && this.searchTrackingVars && Object.keys(this.searchTrackingVars).length >= 0) {
        trackSearch(this.searchTrackingVars, this.searchTrackingUID, this.searchTotalObjects);
      }
    },
    openModalMap() {
      this.mapModalReady = true;
      this.$nextTick(() => {
        this.$modal.show('mapModal');
      });
    },
    clearOverflow() {
      if (document) {
        const { body } = document;
        if (body) {
          body.classList.remove('vue-lb-open');
          body.style.position = '';
          body.style.left = '';
          body.style.top = '';
        }
      }
    },
    // search - need this little method to trigger the dispatch from watch
    search(forceHydrate = true) {
      if (forceHydrate) {
        this.dispatchHydrateAction();
        return this.searchObjectsMounted.then(() => this.dispatchSearchObjects());
        // we do not need to query the store here because the action checks for changes itself
      }
      return this.dispatchSearchObjects();
    },
    dispatchSearchObjects() {
      return this.$store.dispatch('SEARCH_OBJECTS', { router: this.$router })
        .catch((err) => {
          this.$store.commit('SET_APP_APIHTTPSTATUS', { type: 'search', status: err.status });
          // reset filters because SEARCH_OBJECTS check filters to see whether somethign changes
          this.$store.commit('SET_LASTSEARCHFILTER', {});
        });
    },
    // include function from vuex mutations which can be used at several times
    ...mapMutations({
      setMapStatus: 'SET_MAP_STATUS',
    }),
    toggleMap() {
      this.$nextTick(() => {
        this.fullscreenmap = !this.fullscreenmap;
      });
    },
    gtmContentGroupPush() { // create own method
      this.dataLayerPush( // gtm dataLayer push based on EDVUE-156
        {
          event: 'contentGroupPush',
          ContentGroupIndex: 2,
          ContentGroup: 'Suchtrefferliste',
          ContentGroupData: `Suche:${(this.isSeoPage) ? 'SEO:' : ''}${this.country.toUpperCase()}`,
        },
      );
    },
    preloadObjectPage() {
      // ObjectPage();
    },
  },

};
</script>

<style lang="scss" scoped>

.search {
  border-top: 1px solid $color_grey_100;
  margin-top: -1px;

  @media #{$media_lg_min} {
    display: flex;
    height: 100%;
    position: relative;
  }

  .search-list {
    position: relative;

    .search-list-filter {
      background: #fff;
      padding: $margin_content;
      margin: 0 0 2*$margin_content;
      border-top: 1px solid $color_grey_100;
      border-bottom: 1px solid $color_grey_100;

      .btns {
        .btn {
          margin: 0 $margin_content_xs 0 0;
        }
      }
    }

    @media #{$media_lg_min} {
      width: 60%;

      .search-list-filter {
        position: sticky;
        top: 0;
        z-index: 50;
        overflow: auto;
      }
    }

    .search-list-objects {
      padding: $margin_content $margin_content 0;
      position: relative;

      @media #{$media_xs} {
        padding: $margin_content $margin_content_xs 0;
      }

      &.overgrey {
        &::after {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: rgba(#000, 0.3);
          z-index: 100;
          pointer-events: none;
        }
      }
    }
  }
}

.search-map-observer {
  width: 40%;
  position: relative;
}

.search-map-observer.inviewport,
.search-map-observer.inviewport .component-observer-body {
  @media #{$media_sm_max} {
    display: none;
  }
}

.search-map-observer.fullscreen {
  position: fixed;
  width: 100%;
  display: block !important;
  z-index: 150;
  top: 0;
  height: 100%;

  .search-filter-mobile {
    position: fixed;
    z-index: 50;
    top: 0;
  }
}

.grid-criterias-pagination {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 0 $margin_content 0 $margin_content;

  @media #{$media_xs_max} {
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: 1fr;
    .grid-item-criterias-pagination:nth-child(1) { order: 2; }
    .grid-item-criterias-pagination:nth-child(1) { text-align: center; }
    .grid-item-criterias-pagination:nth-child(2) { order: 1; }

    padding: 0 $margin_content_xs 0 $margin_content_xs;

  }
}

/deep/ div.search-list-pagination {
  margin: 0 0 2*$margin_content $margin_content;
  @media #{$media_xs_max} {
    margin: 0 0 $half_margin_content $margin_content;
    & > div {
      margin: $margin_content /2;
    }
  }
}
</style>
